/**
 * @description Component-TradeOrdersMobile
 */
import './index.less';

import _ from 'lodash';
import React, { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import Empty from '@/components/Empty';
import { EmptyDataWrap } from '@/components/EmptyDataWrap';
import SynLink from '@/components/Link';
import { Skeleton } from '@/components/Skeleton';
import TokenPairSimple from '@/components/TokenPair/TokenPairSimple';
import { Tooltip } from '@/components/ToolTip';
import { FETCHING_STATUS } from '@/constants';
import { PAIR_PAGE_TYPE } from '@/constants/global';
import { WrappedOrder } from '@/entities/WrappedOrder';
import { useWrappedPortfolioByPairId } from '@/features/account/portfolioHook';
import { useGlobalConfig, useIsIpBlocked } from '@/features/global/hooks';
import { useCurrentPairFromUrl } from '@/features/pair/hook';
import { useFetchPortfolioListFromApi } from '@/features/portfolio/query';
import { cancelTradeOrders } from '@/features/trade/actions';
import { useCancelFormStatus } from '@/features/trade/hooks';
import { useWalletConnectStatus } from '@/features/wallet/hook';
import { useAppProvider, useSDK } from '@/features/web3/hook';
import { useAppDispatch } from '@/hooks';
import useOpenOrderListWithCreateTime from '@/hooks/portfolio/useOpenOrderListWithCreateTime';
import { useFilteredOrders } from '@/hooks/trade/useFilteredOrders';
import { useSideNavigate } from '@/hooks/useRouterNavigate';
import { useTxNotification } from '@/hooks/useTxNotification';
import { useChainId, useDappChainConfig, useEtherscanLink, useUserAddr } from '@/hooks/web3/useChain';
import { useSwitchNetwork } from '@/hooks/web3/useSwitchConnectorNetwork';
import { useWalletSigner } from '@/hooks/web3/useWalletNetwork';
import { TabKeys } from '@/pages/trade/TradePortfolio/TradePortfolioTabs';
import { WALLET_CONNECT_STATUS } from '@/types/wallet';
import { formatDate } from '@/utils/timeUtils';
interface IPropTypes {
  children?: React.ReactNode;
  className?: string;
  isTradePage?: boolean;
}
const TradeOrdersMobile: FC<IPropTypes> = function ({ isTradePage = true }) {
  const { t } = useTranslation();
  const chainId = useChainId();
  const userAddr = useUserAddr();
  const dispatch = useAppDispatch();
  const currentPair = useCurrentPairFromUrl(chainId);
  const sdkContext = useSDK(chainId);
  const provider = useAppProvider();
  const ipBlocked = useIsIpBlocked();
  const { deadline } = useGlobalConfig(chainId);
  const signer = useWalletSigner();

  const portfolio = useWrappedPortfolioByPairId(chainId, userAddr, currentPair?.id);
  const openOrders = useFilteredOrders(currentPair, isTradePage);
  const openOrdersWithCreateTime = useOpenOrderListWithCreateTime(openOrders);
  const { isFetched: isFetchedPortfolio } = useFetchPortfolioListFromApi(chainId, userAddr);
  const { pairPageNavigate } = useSideNavigate();

  const notification = useTxNotification();
  const getTxLink = useEtherscanLink();

  const cancelStatuses = useCancelFormStatus(chainId);

  const walletStatus = useWalletConnectStatus();
  const { switchWalletNetwork } = useSwitchNetwork();
  const dappConfig = useDappChainConfig(chainId);
  const cancelOrders = useCallback(
    async (orders: WrappedOrder[]) => {
      if (chainId && userAddr && sdkContext && signer && portfolio && deadline && provider) {
        try {
          if (walletStatus === WALLET_CONNECT_STATUS.WRONG_NETWORK) {
            await switchWalletNetwork(chainId);
            return;
          }
          const result = await dispatch(
            cancelTradeOrders({
              chainId,
              userAddr,
              sdkContext,
              signer,
              portfolio,
              orders: orders,
              deadline: Number(deadline),
              provider,
              sendTxNotification: notification?.open,
            }),
          ).unwrap();
          if (result) {
            // setSelectedOrder(undefined);
          }
        } catch (e) {}
      }
    },
    [
      chainId,
      deadline,
      dispatch,
      notification?.open,
      portfolio,
      provider,
      sdkContext,
      signer,
      switchWalletNetwork,
      userAddr,
      walletStatus,
    ],
  );
  if (!isFetchedPortfolio) {
    return (
      <div className="syn-trade-orders-mobile-skeleton">
        <div className="syn-trade-orders-mobile-skeleton-top">
          <Skeleton active={true} paragraph={false} />
          <Skeleton active={true} paragraph={false} />
        </div>
        <div className="syn-trade-orders-mobile-skeleton-row">
          <Skeleton active={true} paragraph={false} />
          <Skeleton active={true} paragraph={false} />
          <Skeleton active={true} paragraph={false} />
        </div>
      </div>
    );
  }
  return (
    <div className="syn-trade-orders-mobile">
      {!openOrders?.length && <Empty type="vertical" />}
      {openOrdersWithCreateTime?.map((order) => {
        const cancelStatus = _.get(cancelStatuses, [order?.id || '']);
        return (
          <div
            className="syn-mobile-profile-item"
            key={order.id}
            onClick={() => {
              pairPageNavigate(order.rootPair.symbol, PAIR_PAGE_TYPE.TRADE, undefined, {
                state: {
                  tab: TabKeys.OPEN_ORDERS,
                },
              });
            }}>
            <div className="syn-mobile-profile-item-header">
              <div className="syn-mobile-profile-item-title">
                {' '}
                <EmptyDataWrap isLoading={!order?.rootPair}>
                  <TokenPairSimple
                    tradeSide={order.wrappedSide}
                    // leverage={order.leverage}
                    isInverse={order?.rootPair.isInverse}
                    baseToken={order?.rootPair?.rootInstrument.baseToken}
                    quoteToken={order?.rootPair?.rootInstrument.quoteToken}
                  />
                </EmptyDataWrap>
              </div>
              <div className="syn-mobile-profile-item-btns">
                <Tooltip showOnMobile={true} title={ipBlocked ? t('common.ipBlocker.tooltip') : undefined}>
                  <Button
                    ghost
                    disabled={ipBlocked}
                    onClick={(e) => {
                      e.stopPropagation();
                      cancelOrders([order]);
                    }}
                    loading={cancelStatus === FETCHING_STATUS.FETCHING}
                    className="syn-trade-open-orders-cancel-btn">
                    {t(
                      walletStatus === WALLET_CONNECT_STATUS.WRONG_NETWORK ? 'common.switchToChain' : 'common.cancel',
                      {
                        chainName: dappConfig?.network?.name || '',
                      },
                    )}
                  </Button>
                </Tooltip>
              </div>
            </div>
            <div className="syn-mobile-profile-item-body">
              <dl>
                <dt>{t('common.time')}</dt>
                <dd>
                  {(() => {
                    const createTimestamp = order.lastUpdateTime || 0;
                    const txLink = getTxLink(order.lastUpdateTxHash || '', 'transaction');
                    return (
                      <SynLink target="_blank" href={txLink}>
                        {createTimestamp ? formatDate(createTimestamp * 1000) : '-'}
                      </SynLink>
                    );
                  })()}
                </dd>
              </dl>
              <dl>
                <dt>{t('common.size')} </dt>
                <dd>
                  <span style={{ color: order.wrappedQuoteSize.gte(0) ? '#14b84b' : '#ff6666' }}>
                    {order?.wrapAttribute('size')?.abs()?.formatNumberWithTooltip({
                      suffix: order.rootPair.rootInstrument.baseToken.symbol,
                      isShowTBMK: true,
                    })}
                  </span>
                </dd>
              </dl>
              <dl>
                <dt> {t('common.taken')}</dt>
                <dd>
                  <div className="filled-bottom">
                    {order.wrappedFilledSize.formatNumberWithTooltip({
                      suffix: order.rootPair.rootInstrument.baseToken.symbol,
                      isShowTBMK: true,
                    })}
                  </div>
                </dd>
              </dl>
              <dl>
                <dt>{t('common.table.orderPrice')} </dt>
                <dd>{order.wrapAttribute('limitPrice').formatPriceNumberWithTooltip()} </dd>
              </dl>
              <dl>
                <dt>{t('common.table.margin')} </dt>
                <dd>
                  {order?.wrapAttribute('balance')?.formatNumberWithTooltip({
                    suffix: order.rootPair.rootInstrument.marginToken.symbol,
                  })}
                </dd>
              </dl>
              <dl>
                <dt>{t('common.table.leverage')} </dt>
                <dd>{order.wrapAttribute('leverageWad').formatLeverageWithTooltip()} </dd>
              </dl>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TradeOrdersMobile;
